// Mock data for Trusted Companion Health Care (frontend-only, original content)

export const company = {
  name: 'Trusted Companion Health Care',
  shortName: 'Trusted Companion',
  legalName: 'Trusted Companion Health Care Limited',
  phones: ['+353 87 096 2889'],
  primaryPhone: '+353 87 096 2889',
  email: 'contact@trustedcompanionhc.net',
  address: '8 Yorke Street, Dundalk, County Louth, A91 TT68',
  mapUrl: 'https://maps.google.com/?q=8+Yorke+Street,+Dundalk,+Co.+Louth,+Ireland',
  androidUrl: '#android-app',
  appleUrl: '#ios-app',
  // Hero copy — first part normal, highlight rendered in mint accent
  heroLead: 'Compassionate care,',
  heroHighlight: 'made personal.',
  subTagline: 'Thoughtful home care and dependable healthcare staffing for families and providers right across Ireland.',
  tagline: 'Compassionate care, made personal.',
};

export const navLinks = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/about' },
  { label: 'Services', to: '/services' },
  { label: 'Careers', to: '/careers' },
  { label: 'Contact Us', to: '/contact' },
];

export const heroImage = '/generated/hero.png';

export const heroStats = [
  { value: '100%', label: 'Garda-vetted carers' },
  { value: '20+', label: 'Counties covered' },
];

export const welcomeHighlights = [
  'Care plans shaped around each individual',
  'Skilled, Garda-vetted, mentored carers',
  'Flexible support, day or night',
  'One dependable point of contact',
];

export const welcomeImages = {
  main: '/generated/welcome-main.png',
  hands: '/generated/welcome-hands.png',
};

export const whyChooseImage = '/generated/whychoose.png';

export const aboutImage = '/generated/about.png';

export const aboutText = {
  heading: 'A companion you can count on',
  paragraphs: [
    'Trusted Companion Health Care Limited helps people across Ireland stay safe, comfortable and independent in the place that matters most — their own home.',
    'We pair genuinely caring people with the training, structure and reliability that families deserve. The result is calm, consistent support for the people we look after, and dependable staffing for the providers who partner with us.',
  ],
};

export const whyChoose = [
  {
    icon: 'HeartHandshake',
    title: 'Care shaped around each person',
    desc: 'We start with the individual — their routine, their wishes and the outcomes that matter — then build the right support around them.',
  },
  {
    icon: 'ShieldCheck',
    title: 'Skilled, carefully vetted people',
    desc: 'Every carer is Garda-vetted, reference-checked and supported with ongoing training, supervision and mentoring.',
  },
  {
    icon: 'Zap',
    title: 'Reliable cover, precisely matched',
    desc: 'Need staffing at short notice? We match qualified professionals to your service quickly — without ever compromising on quality.',
  },
  {
    icon: 'MapPin',
    title: 'Proudly local, across Ireland',
    desc: 'From cities to smaller communities, we are here for families, care homes and providers in towns right across the country.',
  },
];

export const services = [
  {
    num: '01',
    icon: 'Users',
    title: 'Community Support',
    desc: 'Flexible support out in the community that keeps people active, connected and confident close to home.',
    image: '/generated/svc-community.png',
    slug: 'community-support',
  },
  {
    num: '02',
    icon: 'Home',
    title: 'Independent Living',
    desc: 'Everyday help with routines and tasks so people can keep living life confidently and on their own terms.',
    image: '/generated/svc-independent.png',
    slug: 'independent-living',
  },
  {
    num: '03',
    icon: 'HeartHandshake',
    title: 'Home Care',
    desc: 'Warm, dependable personal care delivered in the comfort and familiarity of your own home.',
    image: '/generated/svc-homecare.png',
    slug: 'home-care',
  },
  {
    num: '04',
    icon: 'Accessibility',
    title: 'Disability & Complex Needs',
    desc: 'Specialist, carefully-planned support for people with intellectual disabilities and complex care needs.',
    image: '/generated/svc-disability.png',
    slug: 'disability-support',
  },
  {
    num: '05',
    icon: 'Armchair',
    title: 'Respite Care',
    desc: 'Trusted short-term cover that gives family carers a genuine break and complete peace of mind.',
    image: '/generated/svc-respite.png',
    slug: 'respite',
  },
  {
    num: '06',
    icon: 'Brain',
    title: 'Brain Injury & Neuro Support',
    desc: 'Considered, specialist support for people living with acquired brain injury and neurodiverse conditions.',
    image: '/generated/svc-brain.png',
    slug: 'brain-injury',
  },
  {
    num: '07',
    icon: 'HeartPulse',
    title: 'Mental Health Support',
    desc: 'Gentle, person-led support focused on wellbeing, recovery and everyday confidence.',
    image: '/generated/svc-mental.png',
    slug: 'mental-health',
  },
  {
    num: '08',
    icon: 'ShieldCheck',
    title: 'Social Care',
    desc: 'Structured, professional care for individuals with more complex social circumstances and needs.',
    image: '/generated/svc-social.png',
    slug: 'social-care',
  },
  {
    num: '09',
    icon: 'Coffee',
    title: 'Companionship',
    desc: 'Friendly visits and real connection that ease loneliness and bring warmth to everyday life.',
    image: '/generated/svc-companion.png',
    slug: 'companionship',
  },
  {
    num: '10',
    icon: 'UserPlus',
    title: 'Healthcare Staffing',
    desc: 'Dependable staffing for care homes and providers — qualified, vetted professionals when you need them.',
    image: '/generated/svc-staffing.png',
    slug: 'staffing',
  },
];

export const serviceDetails = {
  'community-support': {
    overview: 'Our Community Support helps people stay active and connected in the place they know best. Whether it is getting to appointments, joining a local group, doing the weekly shop or simply enjoying a walk, our support workers are there to make everyday life easier and more sociable.',
    points: [
      'Help getting out and about safely in the local area',
      'Support with appointments, shopping and errands',
      'Encouragement to join clubs, groups and activities',
      'Building confidence and reducing social isolation',
    ],
  },
  'independent-living': {
    overview: 'Support in Living Independently is about doing things with people, not for them. We provide gentle, practical help with daily routines so individuals can keep their independence, dignity and confidence at home for as long as possible.',
    points: [
      'Support with cooking, cleaning and household tasks',
      'Help managing routines, medication reminders and bills',
      'Skills-building to grow day-to-day confidence',
      'Regular reviews to adapt support as needs change',
    ],
  },
  'home-care': {
    overview: 'Our Domiciliary (Home) Care brings compassionate, professional care right to your door. From a short daily visit to full-time live-in support, we tailor everything to the individual so they can remain comfortable and safe in familiar surroundings.',
    points: [
      'Personal care such as washing, dressing and mobility',
      'Meal preparation and support with nutrition',
      'Companionship and emotional support',
      'Flexible visits, from a few hours to live-in care',
    ],
  },
  'disability-support': {
    overview: 'We provide specialist support for people with intellectual disabilities and complex needs, built around each person\u2019s goals. Our trained team focuses on independence, communication and quality of life, working closely with families and other professionals.',
    points: [
      'Person-centred plans developed with the individual',
      'Support with communication and daily living skills',
      'Positive behaviour and complex-needs experience',
      'Close coordination with families and care teams',
    ],
  },
  'respite': {
    overview: 'Respite Services give family carers a well-earned break, with complete peace of mind that their loved one is in safe, capable hands. We offer planned or short-notice cover, in the home or as part of a longer stay, tailored to your situation.',
    points: [
      'Planned or short-notice respite cover',
      'In-home support or extended care arrangements',
      'Continuity through familiar, trusted carers',
      'A genuine chance for carers to rest and recharge',
    ],
  },
  'brain-injury': {
    overview: 'Our support for people living with acquired brain injury and neurodiverse conditions is patient, structured and specialist. We help individuals rebuild routines, confidence and independence at a pace that feels right for them.',
    points: [
      'Structured routines and cognitive support',
      'Rehabilitation-focused, goal-led approach',
      'Experienced, specially-trained support workers',
      'Support for both the individual and their family',
    ],
  },
  'mental-health': {
    overview: 'Mental Health Support is person-led and focused on wellbeing. We offer a calm, non-judgemental presence and practical help that promotes recovery, stability and everyday confidence, always at the individual\u2019s pace.',
    points: [
      'Warm, non-judgemental one-to-one support',
      'Help re-establishing routines and structure',
      'Encouragement to reconnect with activities and people',
      'A steady, reassuring point of contact',
    ],
  },
  'social-care': {
    overview: 'Our Forensic and Social Care service provides structured, professional support for individuals with more complex social care requirements. We work sensitively and collaboratively to keep people safe, supported and moving forward.',
    points: [
      'Structured support for complex social needs',
      'Risk-aware, professional and confidential',
      'Close working with statutory and care partners',
      'A focus on stability, safety and progress',
    ],
  },
  'companionship': {
    overview: 'Befriending and Companionship is all about connection. A friendly, familiar face can transform someone\u2019s week \u2014 sharing a cup of tea, a hobby or a conversation \u2014 easing loneliness and bringing real warmth to everyday life.',
    points: [
      'Regular friendly visits and conversation',
      'Shared hobbies, outings and activities',
      'Reduced loneliness and improved wellbeing',
      'Consistent, familiar companions',
    ],
  },
  'staffing': {
    overview: 'Our Recruitment & Staffing service supplies care homes and providers with qualified, vetted healthcare professionals \u2014 quickly and reliably. Every candidate is carefully screened so you get quality cover without the stress.',
    points: [
      'Garda-vetted, reference-checked professionals',
      'Fast cover for planned or urgent shifts',
      'Nurses, healthcare assistants and support workers',
      'Reliable, quality-assured staffing you can trust',
    ],
  },
};

export const testimonials = [
  {
    quote: 'They gave my mother the confidence to stay in her own home. Kind, professional and reliable, every single visit.',
    name: 'Siobhan D.',
    location: 'Cork',
    avatar: '/generated/avatar-1.png',
  },
  {
    quote: 'Real warmth paired with real professionalism. For the first time I feel genuinely supported rather than just managed.',
    name: 'Liam B.',
    location: 'Limerick',
    avatar: '/generated/avatar-2.png',
  },
  {
    quote: 'Their respite cover let me rest without a moment of worry. I could not recommend the team more highly.',
    name: 'Grainne M.',
    location: 'Kilkenny',
    avatar: '/generated/avatar-3.png',
  },
];

export const vacancies = [
  { title: 'Registered General Nurse (RGN)', location: 'Dublin', type: 'Full-time', tag: 'Nursing' },
  { title: 'Healthcare Assistant', location: 'Cork', type: 'Full-time / Part-time', tag: 'Care' },
  { title: 'Disability Support Worker', location: 'Meath', type: 'Full-time', tag: 'Disability' },
  { title: 'Community Care Worker', location: 'Galway', type: 'Part-time', tag: 'Community' },
  { title: 'Mental Health Support Worker', location: 'Limerick', type: 'Full-time', tag: 'Mental Health' },
  { title: 'Live-in Carer', location: 'Nationwide', type: 'Contract', tag: 'Home Care' },
];

export const careerPerks = [
  { icon: 'CalendarClock', title: 'Flexible Scheduling', desc: 'Pick up shifts that fit around your life with simple, transparent scheduling.' },
  { icon: 'GraduationCap', title: 'Grow With Us', desc: 'Keep developing through ongoing training and recognised qualifications.' },
  { icon: 'BadgeEuro', title: 'Fair, Reliable Pay', desc: 'Enjoy fair rates and dependable, on-time pay for the work you do.' },
  { icon: 'Users', title: 'A Team That Cares', desc: 'Join a supportive, close-knit community of dedicated care professionals.' },
];
